/**
 * GLSL for approximate soft shadows with varying penumbra.
 * @module
 */
import { ShaderChunk } from "three";
import { safeShaderReplace } from "../shaderUtils";

const advancedPCSSFunctions = `
#if defined(USE_ADVANCED_PCSS) && USE_ADVANCED_PCSS == 1
// Suggested settings for higher shadow quality: NUM_SAMPLES 13 or 17, NUM_RINGS 5
#ifndef PCSS_NUM_SAMPLES
#define PCSS_NUM_SAMPLES 7
#endif
#ifndef PCSS_NUM_RINGS
#define PCSS_NUM_RINGS 5
#endif

// Ad-hoc parameter: how far in the shadow map to look for blockers. This also affects max penumbra size in terms of
// shadow map pixels.
#define SHADOW_BLOCKER_SEARCH_MULTIPLIER 4.0
uniform float softShadowCameraFar;

const float INV_PCSS_NUM_SAMPLES = 1.0 / float( PCSS_NUM_SAMPLES );
vec2 poissonDisk[PCSS_NUM_SAMPLES];

void initPoissonDisk( const in vec2 randomSeedUv ) {
  float angle = rand( randomSeedUv + randomSeed ) * PI2;

#if PCSS_NUM_SAMPLES == 7
  const vec2 poissonDiskSrc[7] = vec2[7](
    vec2(-0.05170676223720603 -0.22654212732855875),
    vec2(0.3520941029558192, 0.16955958341532756),
    vec2(0.33025299538517516, 0.4141241576073132),
    vec2(0.4097800563179557, -0.5138479377879064),
    vec2(-0.7000253346306389, -0.33711443368036187),
    vec2(-0.19822607504618509, 0.8684851804671821),
    vec2(1, 0)
  );
  float c = cos(angle);
  float s = sin(angle);
  mat2 rotationMat = mat2(c, s, -s, c);
  for( int i = 0; i < PCSS_NUM_SAMPLES; i ++ ) {
    poissonDisk[i] = rotationMat * poissonDiskSrc[i];
  }
#else
  const float ANGLE_STEP = PI2 * float( PCSS_NUM_RINGS ) * INV_PCSS_NUM_SAMPLES;

  // jsfiddle that shows sample pattern: https://jsfiddle.net/j46rxmfw/
  float radius = INV_PCSS_NUM_SAMPLES;
  float radiusStep = radius;

  for( int i = 0; i < PCSS_NUM_SAMPLES; i ++ ) {
    poissonDisk[i] = vec2( cos( angle ), sin( angle ) ) * pow( radius, 0.75 );
    radius += radiusStep;
    angle += ANGLE_STEP;
  }
#endif
}

float findBlocker( sampler2D shadowMap, vec2 texelSize, const in vec2 uv, const in float zReceiver, float shadowRadius ) {
  vec2 searchRadius = texelSize * shadowRadius * SHADOW_BLOCKER_SEARCH_MULTIPLIER;

  float blockerDepthSum = 0.0;

  float totalWeight = 0.0;

  for( int i = 0; i < PCSS_NUM_SAMPLES; i++ ) {
    float shadowMapDepth = texture2DLodEXT(shadowMap, uv + poissonDisk[i] * searchRadius, 0.0 ).r;
    if ( shadowMapDepth < zReceiver ) {
      // We add more weight to blockers closer to the filter center. This reduces artifacts.
#if PCSS_NUM_SAMPLES == 7
      float weight = 2.0 - float(i) * 0.17;
#else
      float weight = (2.0 - pow( length(poissonDisk[i]), 2.0 ));
#endif
      blockerDepthSum += shadowMapDepth * weight;

      totalWeight += weight;
    }
  }

  if( totalWeight == 0.0 ) return -1.0;

  return blockerDepthSum / totalWeight;
}

float advancedPCFFilter(sampler2D shadowMap, vec2 uv, float zReceiver, vec2 filterScale ) {
  float sum = 0.0;
  for( int i = 0; i < PCSS_NUM_SAMPLES; i ++ ) {
    float depth = texture2DLodEXT( shadowMap, uv + poissonDisk[ i ] * filterScale, 0.0 ).r;
#if defined(USE_SMOOTHSTEP_SHADOW_COMPARISON) && USE_SMOOTHSTEP_SHADOW_COMPARISON == 1
    sum += smoothstep( zReceiver - 0.001, zReceiver + 0.001, depth );
#else
    sum += step( zReceiver, depth );
#endif
  }
  return sum * INV_PCSS_NUM_SAMPLES;
}

float getShadowAdvancedPCSS( sampler2D shadowMap, vec4 shadowCoord, vec2 shadowMapSize, float shadowRadius ) {
  // shadowCoord.z is assumed to be eye-space z scaled to be relative to the depth buffer in this code.
  float zReceiver = shadowCoord.z;

  initPoissonDisk( shadowCoord.xy );

  vec2 texelSize = 1.0 / shadowMapSize;

#if defined(PCSS_PENUMBRA_AFFECTED_BY_DISTANCE) && PCSS_PENUMBRA_AFFECTED_BY_DISTANCE == 1
  float avgBlockerDepth = findBlocker( shadowMap, texelSize, shadowCoord.xy, zReceiver, shadowRadius );

  // There are no occluders so early out.
  if( avgBlockerDepth == -1.0 ) return 1.0;

  float penumbraRatio = ( zReceiver - avgBlockerDepth ) * softShadowCameraFar;
  float filterRadius = max( penumbraRatio * shadowRadius, 2.0 );
  filterRadius = min( filterRadius, shadowRadius * SHADOW_BLOCKER_SEARCH_MULTIPLIER * 1.1 );

  vec2 filterScale = filterRadius * texelSize;
#else
  vec2 filterScale = shadowRadius * 2.0 * texelSize;
#endif

  return advancedPCFFilter( shadowMap, shadowCoord.xy, zReceiver, filterScale );
}
#endif  // USE_ADVANCED_PCSS
`;

// Our custom PCSS shadow function body (without signature - will be combined with extracted signature)
const pcssShadowFunctionBodyContent = `
	float shadow = 1.0;

	shadowCoord.xyz /= shadowCoord.w;
	shadowCoord.z += shadowBias;

	bool inFrustum = shadowCoord.x >= 0.0 && shadowCoord.x <= 1.0 && shadowCoord.y >= 0.0 && shadowCoord.y <= 1.0;
	bool frustumTest = inFrustum && shadowCoord.z <= 1.0;

	if ( frustumTest ) {
		float shadowMapShadowPCSS = getShadowAdvancedPCSS( shadowMap, shadowCoord, shadowMapSize, shadowRadius );
		#if defined(SCREEN_SPACE_SHADOWS) && SCREEN_SPACE_SHADOWS == 1
		float screenSpaceShadow = ssfxEnabled ? texture2D(screenSpaceShadowMap, (gl_FragCoord.xy + aoMapPixelOffset) * aoMapInvSize).r : 1.0;
		shadow = min(shadowMapShadowPCSS, screenSpaceShadow);
		#else
		shadow = shadowMapShadowPCSS;
		#endif
	}

	return mix( 1.0, shadow, shadowIntensity );
`;

// Matches the #else branch containing the basic shadow function (sampler2D, not sampler2DShadow).
// Capture groups: $1=else directive, $2=function signature+brace, $3=body, $4=closing brace, $5=#endif
const basicShadowBlockRegex =
  /(#else\s*(?:\/\/[^\n]*)?\s*)(float\s+getShadow\s*\(\s*sampler2D\s+shadowMap\s*,\s*vec2\s+shadowMapSize\s*,\s*float\s+shadowIntensity\s*,\s*float\s+shadowBias\s*,\s*float\s+shadowRadius\s*,\s*vec4\s+shadowCoord\s*\)\s*\{)([\s\S]*?)(\}\s*)(#endif)/;

const pcssShadowReplacement =
  `$1#if defined(USE_ADVANCED_PCSS) && USE_ADVANCED_PCSS == 1\n` +
  `$2${pcssShadowFunctionBodyContent}$4\n` +
  `#else\n` +
  `$2$3$4\n` +
  `#endif\n` +
  `$5`;

export const customShadowmapParsFragChunk = (() => {
  let chunk = ShaderChunk.shadowmap_pars_fragment;

  // Inject our advanced PCSS helper functions after #ifdef USE_SHADOWMAP
  chunk = safeShaderReplace(
    chunk,
    "#ifdef USE_SHADOWMAP",
    "#ifdef USE_SHADOWMAP" + advancedPCSSFunctions,
    "softShadow.ts: advanced PCSS helper injection into shadowmap_pars_fragment",
  );

  // Three.js r175+ removed SHADOWMAP_TYPE_PCF_SOFT. The new shadow system uses:
  // - SHADOWMAP_TYPE_PCF: sampler2DShadow with hardware comparison (incompatible with our PCSS)
  // - SHADOWMAP_TYPE_VSM: variance shadow maps
  // - else (BASIC): sampler2D with manual depth comparison (compatible with our PCSS)
  //
  // Our advanced PCSS uses texture2DLodEXT to read raw depth values, which requires sampler2D.
  // We inject our custom shadow function into the #else branch (basic shadow path).
  // The renderer must use BasicShadowMap when USE_ADVANCED_PCSS is enabled.
  //
  // r185 added USE_REVERSED_DEPTH_BUFFER branches to the upstream body. Only the #else (non-PCSS)
  // branch below keeps that body; the PCSS branch replaces it with a fixed standard-depth
  // comparison. Enabling a reversed depth buffer would need the bias sign and depth test here
  // flipped too, so initTHREEJSRenderer asserts that it is never enabled.
  chunk = safeShaderReplace(
    chunk,
    basicShadowBlockRegex,
    pcssShadowReplacement,
    "softShadow.ts: PCSS shadow injection into shadowmap_pars_fragment",
  );

  return chunk;
})();
